import React from 'react';
import Helmet from 'react-helmet';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import RelatedLinks from '../components/RelatedLinks';

const RegistrationGuide = () => {
  const pageTitle = '魔鬼鱼订货平台注册指南 - 宠物行业 B2B 商户入驻';
  const pageDescription = '魔鬼鱼宠物 B2B 订货平台面向全国宠物行业 B 端正规经营商户开放入驻。支持微信小程序注册，1 个工作日完成审核。';

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '魔鬼鱼订货平台',
    url: 'https://www.mantas.cn',
    description: '宠物行业一站式B2B订货平台，为宠物店、宠物医院、猫咖、繁育舍提供靠谱宠物进货渠道',
    areaServed: 'CN',
    knowsAbout: ['宠物用品批发', '宠物店进货', '宠物B2B平台', '宠物用品订货']
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://www.mantas.cn'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '注册指南',
        item: 'https://www.mantas.cn/registration-guide/'
      }
    ]
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '哪些商户可以入驻魔鬼鱼订货平台？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '支持入驻商户类型包含：线下宠物店（宠物洗护美容门店/宠物用品店/宠物生活馆）、宠物医院（宠物诊疗机构/动物医院/宠物药房等）、猫犬舍（猫咖猫舍/家庭繁育犬舍/家庭繁育猫舍等）、宠物寄养中心、宠物救助机构、宠物电商商家（拼多多/淘宝等平台电商卖家）。'
        }
      },
      {
        '@type': 'Question',
        name: '在哪里可以申请注册魔鬼鱼订货平台进行采购？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '打开微信，搜索小程序「魔鬼鱼订货平台」，或者扫描平台官方注册二维码，按照页面指引选择对应商户类型、提交经营资质材料，发起入驻申请。'
        }
      },
      {
        '@type': 'Question',
        name: '提交注册之后多久可以审核完成？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '正常资料提交完成后，平台会在 1 个工作日内完成入驻审核，请留意小程序消息通知。资料缺失、资质不符合条件会直接驳回，需要修改重提。'
        }
      },
      {
        '@type': 'Question',
        name: '商户类型选错提交审核，还可以修改吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '提交审核之前务必选择和自身经营身份匹配的商户类型。若已经提交被驳回，可以回到小程序注册页面，重新选择商户类型、更新资料再次提交；审核通过后商户类型变更需要联系客服人工处理。'
        }
      },
      {
        '@type': 'Question',
        name: '宠物救助机构可以入驻申请吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '可以。正规宠物救助机构、动物保护组织可申请入驻，选择对应商户类型，提交相关资料参与审核即可。'
        }
      }
    ]
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '注册指南',
    description: '魔鬼鱼宠物B2B订货平台面向全国宠物行业B端正规经营商户开放入驻。支持微信小程序注册，1个工作日完成审核。',
    specialty: 'RegistrationGuide'
  };

  // HowTo Schema - 注册步骤
  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: '如何在魔鬼鱼订货平台注册入驻',
    description: '魔鬼鱼订货平台面向全国宠物行业B端正规经营商户开放入驻，简单四步即可完成注册。',
    totalTime: 'P1D', // 1个工作日
    step: [
      {
        '@type': 'HowToStep',
        position: 1,
        name: '获取注册入口',
        text: '微信直接搜索【魔鬼鱼订货平台】小程序或直接扫码上方二维码注册'
      },
      {
        '@type': 'HowToStep',
        position: 2,
        name: '选择商户类型',
        text: '准确选择与自身经营匹配的商户真实类型'
      },
      {
        '@type': 'HowToStep',
        position: 3,
        name: '填写资质信息',
        text: '按要求上传营业执照、法人身份证等经营资质材料，并填写真实经营信息'
      },
      {
        '@type': 'HowToStep',
        position: 4,
        name: '提交审核',
        text: '确认信息无误后提交入驻申请，平台会在1个工作日内完成审核'
      }
    ]
  };

  // Service Schema - B2B订货平台服务
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: '魔鬼鱼宠物B2B订货平台',
    description: '面向全国宠物行业B端正规经营商户的一站式宠物用品订货平台',
    provider: {
      '@type': 'Organization',
      name: '魔鬼鱼订货平台'
    },
    areaServed: {
      '@type': 'Country',
      name: '中国'
    },
    serviceType: 'B2B宠物用品批发订货',
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: '宠物用品批发',
      itemListElement: [
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '宠物食品批发' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '宠物用品批发' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '宠物医疗用品批发' } }
      ]
    }
  };

  const merchantTypes = [
    {
      title: '线下宠物店',
      desc: '宠物洗护美容门店/宠物用品店/宠物生活馆',
      icon: '🏪'
    },
    {
      title: '宠物医院',
      desc: '宠物诊疗机构/动物医院/宠物药房等',
      icon: '🏥'
    },
    {
      title: '猫犬舍',
      desc: '猫咖猫舍/家庭繁育犬舍/家庭繁育猫舍等',
      icon: '🏠'
    },
    {
      title: '宠物寄养中心',
      desc: '宠物寄养/宠物酒店等',
      icon: '🏨'
    },
    {
      title: '宠物救助机构',
      desc: '宠物救助/动物保护组织',
      icon: '❤️'
    },
    {
      title: '宠物电商商家',
      desc: '拼多多/淘宝等平台电商卖家',
      icon: '🛒'
    }
  ];

  const steps = [
    {
      number: 1,
      title: '获取注册入口',
      desc: '微信直接搜索【魔鬼鱼订货平台】小程序或直接扫码上方二维码注册'
    },
    {
      number: 2,
      title: '选择商户类型',
      desc: '准确选择与自身经营匹配的商户真实类型'
    },
    {
      number: 3,
      title: '提交入驻资料',
      desc: '根据页面提示提交对应入驻资料'
    },
    {
      number: 4,
      title: '等待审核结果',
      desc: '提交后平台将在 1 个工作日内完成资料审核，请留意审核结果通知。'
    }
  ];

  const notices = [
    {
      icon: '✅',
      title: '正规经营',
      desc: '仅正规经营的 B 端商户，可申请入驻。'
    },
    {
      icon: '📝',
      title: '如实填写',
      desc: '注册务必如实填写经营主体信息，信息不实会导致入驻审核驳回；'
    },
    {
      icon: '📱',
      title: '小程序注册',
      desc: '商家注册入口仅存在于微信小程序，官网仅作规则说明，无法直接提交注册。'
    }
  ];

  const faqItems = [
    {
      question: '哪些商户可以入驻魔鬼鱼订货平台？',
      answer: '支持入驻商户类型包含：线下宠物店（宠物洗护美容门店/宠物用品店/宠物生活馆）、宠物医院（宠物诊疗机构/动物医院/宠物药房等）、猫犬舍（猫咖猫舍/家庭繁育犬舍/家庭繁育猫舍等）、宠物寄养中心、宠物救助机构、宠物电商商家（拼多多/淘宝等平台电商卖家）。'
    },
    {
      question: '在哪里可以申请注册魔鬼鱼订货平台进行采购？',
      answer: '打开微信，搜索小程序「魔鬼鱼订货平台」，或者扫描平台官方注册二维码，按照页面指引选择对应商户类型、提交经营资质材料，发起入驻申请。'
    },
    {
      question: '提交注册之后多久可以审核完成？',
      answer: '正常资料提交完成后，平台会在 1 个工作日内完成入驻审核，请留意小程序消息通知。资料缺失、资质不符合条件会直接驳回，需要修改重提。'
    },
    {
      question: '商户类型选错提交审核，还可以修改吗？',
      answer: '提交审核之前务必选择和自身经营身份匹配的商户类型。若已经提交被驳回，可以回到小程序注册页面，重新选择商户类型、更新资料再次提交；审核通过后商户类型变更需要联系客服人工处理。'
    },
    {
      question: '宠物救助机构可以入驻申请吗？',
      answer: '可以。正规宠物救助机构、动物保护组织可申请入驻，选择对应商户类型，提交相关资料参与审核即可。'
    }
  ];

  return (
    <Layout bodyClass="page-registration-guide">
      <SEO title={pageTitle} description={pageDescription} path="/registration-guide/" />
      <Helmet>
        <meta
          name="keywords"
          content="宠物B2B,订货平台，注册指南，商户入驻，微信小程序"
        />
        <link rel="canonical" href="https://www.mantas.cn/registration-guide/" />
        <meta property="og:image" content="https://www.mantas.cn/images/registration-guide/b2b-platform-banner.png" />
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(howToSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      </Helmet>

      <article className="registration-guide">
        {/* ====== 平台 Banner ====== */}
        <section className="rg-banner">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-8 col-12">
                <h1 className="rg-banner__title">魔鬼鱼订货平台注册指南</h1>
                <picture>
                  <source
                    srcSet="/images/2x/registration-guide/b2b-platform-banner.png"
                    media="(min-resolution: 2dppx)"
                  />
                  <img
                    className="rg-banner__img"
                    src="/images/1x/registration-guide/b2b-platform-banner.png"
                    alt="魔鬼鱼宠物B2B订货平台 — 面向全国宠物行业B端正规经营商户开放入驻"
                    loading="eager"
                  />
                </picture>
              </div>
            </div>
          </div>
        </section>

        {/* ====== 入驻说明区域（商户类型） ====== */}
        <section className="rg-merchant-types">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-8 col-12">
                <h2 className="rg-section-title">适合哪些商户？</h2>
                <p className="rg-section-desc">
                  魔鬼鱼订货平台面向以下类型的正规经营商户开放注册
                </p>
                <div className="row rg-merchant-types__grid">
                  {merchantTypes.map((type, index) => (
                    <div className="col-lg-4 col-md-6 col-12" key={index}>
                      <article className="rg-merchant-card">
                        <div className="rg-merchant-card__icon">
                          <span className="rg-merchant-card__emoji">{type.icon}</span>
                        </div>
                        <div className="rg-merchant-card__content">
                          <h3 className="rg-merchant-card__title">{type.title}</h3>
                          <p className="rg-merchant-card__desc">{type.desc}</p>
                        </div>
                      </article>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ====== 注册步骤区域 ====== */}
        <section className="rg-steps">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-8 col-12">
                <div className="rg-steps__inner">
                  <h2 className="rg-section-title">注册步骤</h2>
                  <p className="rg-section-desc">简单四步，轻松完成注册</p>
                  <div className="row rg-steps__list">
                    {steps.map((step, index) => (
                      <div className="col-lg-3 col-md-6 col-12" key={index}>
                        <article className="rg-step-card">
                          <div className="rg-step-card__number-wrapper">
                            <span className="rg-step-card__number">{step.number}</span>
                          </div>
                          <h3 className="rg-step-card__title">{step.title}</h3>
                          <p className="rg-step-card__desc">{step.desc}</p>
                          {index < steps.length - 1 && (
                            <div className="rg-step-card__connector" aria-hidden="true" />
                          )}
                        </article>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ====== 注意事项区域 ====== */}
        <section className="rg-notices">
          <div className="container">
            <div className="row">
              <div className="col-12">
                <h2 className="rg-section-title">注意事项</h2>
              </div>
            </div>
            <div className="row justify-content-center">
              <div className="col-lg-8 col-md-10 col-12">
                <div className="rg-notices__grid">
                  {notices.map((notice, index) => (
                    <div key={index} className="rg-notice-card">
                      <div className="rg-notice-card__icon">
                        <span>{notice.icon}</span>
                      </div>
                      <div className="rg-notice-card__content">
                        <h3 className="rg-notice-card__title">{notice.title}</h3>
                        <p className="rg-notice-card__desc">{notice.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ====== FAQ 区域（默认展开） ====== */}
        <section className="rg-faq" aria-label="常见问题">
          <div className="container">
            <div className="row">
              <div className="col-12">
                <h2 className="rg-section-title">常见问题</h2>
              </div>
            </div>
            <div className="row justify-content-center">
              <div className="col-lg-8 col-12">
                <div className="rg-faq__list">
                  {faqItems.map((faq, index) => (
                    <article key={index} className="rg-faq-item">
                      <h3 className="rg-faq-item__question">{faq.question}</h3>
                      <div className="rg-faq-item__answer">
                        <p>{faq.answer}</p>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ====== 站内互链（GEO 延伸阅读） ====== */}
        <RelatedLinks
          title="延伸阅读"
          links={[
            { label: '供应商入驻', to: '/supplier' },
            { label: '帮助中心', to: '/help' },
          ]}
        />
      </article>
    </Layout>
  );
};

export default RegistrationGuide;